import './Paywall.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Autoplay } from 'swiper';

import 'swiper/swiper.min.css';

import { Button, Header, List, ListItem, Placeholder, ScrollView } from '$uikit';
import {
  useCurrentUserInfo,
  useTimer,
  useTranslator,
  useUnitName,
  useUserInfo,
} from '$hooks';
import {
  AuthPopoverState,
  CurrencyEnum,
  SubscriptionRateModel,
  SubscriptionType,
} from '$store/models';
import {
  classNames,
  formatCurrency,
  isTesterId,
  setStorageItem,
  statReachGoal,
} from '$utils';
import { balancePayActions } from '$store/balancePay';
import { BalancePayAction } from '$store/balancePay/interface';
import { Api } from '$api';
import { Loader } from '$shared/components';
import { Link, useRoute } from 'react-router5';

import { ReactComponent as IcChat } from './assets/chat.svg';
import { ReactComponent as IcCall } from './assets/call.svg';
import { ReactComponent as IcContent } from './assets/content.svg';
import { mainSelector } from '$store/main';
import { IcClock28, IcSuccess72 } from '$assets';
import { FEED, PROFILE } from '$shared/constants/pages';
import { profileActions } from '$store/profile';
import { authSelector } from '$store/auth';
import { ContentPriorityType } from '$store/main/interface';
import { AuthPopoverSimple } from '$core/AuthPopover/AuthPopover';
import { balanceActions } from '$store/balance';
import { goTo } from '$navigation/helper';

SwiperCore.use([Autoplay]);

interface RateInfo {
  period: number;
  monthPrice: number;
  sale: number;
}

const PaywallTimer: FC<{ deadline: number }> = ({ deadline }) => {
  const timer = useTimer(deadline);

  if (Date.now() >= deadline) {
    return <>00:00:00</>;
  }

  return <>{timer}</>;
};

export const Paywall: FC<{ skipClose?: boolean }> = ({ skipClose = false }) => {
  const { route } = useRoute();
  const target = route.params.target;
  const targetModelId = route.params.target_model;
  const targetModel = useUserInfo(targetModelId);
  const currentUser = useCurrentUserInfo();
  const modelNameInstrumental = useUnitName(targetModel, 'instrumental', 'firstName');
  const modelNameGenitive = useUnitName(targetModel, 'genitive', 'firstName');

  const t = useTranslator();
  const [rateSelected, setRateSelected] = useState(2);
  const dispatch = useDispatch();
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [activeSlide, setActiveSlide] = useState(0);
  const { isLogged } = useSelector(authSelector);
  const { decryptKeys } = useSelector(mainSelector);
  const hasSubscription = !!decryptKeys[-1];
  const [canActivateTrial, setCanActivateTrial] = useState(false);
  const [rates, setRates] = useState<SubscriptionRateModel[]>([
    {
      period: 12,
      price: 58,
    },
    {
      period: 6,
      price: 38,
    },
    {
      period: 1,
      price: 8,
    },
  ]);
  const [createdAt, setCreatedAt] = useState(0);
  const [cover, setCover] = useState<null | { url: string; thumbnail: string }>({
    //url: require('./assets/cover.mp4').default,
    url: require('./assets/poster2.png').default,
    thumbnail: require('./assets/poster2.png').default,
  });
  const trialPrice = !!currentUser && currentUser.id % 2 === 1 ? 1 : 1;

  const trialDeadline = useMemo(() => {
    return (createdAt + 86400) * 1000;
  }, [createdAt]);

  useEffect(() => {
    if (Date.now() < trialDeadline && canActivateTrial && currentUser) {
      if (currentUser.id % 2 === 1) {
        statReachGoal('trial_small_price');
      } else {
        statReachGoal('trial_normal_price');
      }
    }
  }, [trialDeadline, canActivateTrial, currentUser]);

  const slides = useMemo(() => {
    const sliders = [
      {
        id: 'post',
        title: targetModel
          ? t('paywall_slide_content_title_with_name', { name: modelNameGenitive })
          : t('paywall_slide_content_title'),
        caption: t('paywall_slide_content_caption'),
        icon: IcContent,
        canShowAvatar: true,
      },
      {
        id: 'message',
        title: targetModel
          ? t('paywall_slide_chat_title_with_name', { name: modelNameInstrumental })
          : t('paywall_slide_chat_title'),
        caption: t('paywall_slide_chat_caption'),
        icon: IcChat,
        canShowAvatar: true,
      },
      {
        id: 'call',
        title: t('paywall_slide_call_title'),
        caption: t('paywall_slide_call_caption'),
        icon: IcCall,
      },
    ];

    if (target) {
      sliders.sort((a, b) => (a.id === target ? -1 : 1));
    }

    return sliders;
  }, [t, targetModel, modelNameInstrumental, target]);

  const loadRates = useCallback(() => {
    Api.get<any, any>('/balance/subscription/price', {
      params: {
        modelId: targetModelId,
      },
    })
      .then((data) => {
        setRates(data.rates);
        setCanActivateTrial(data.canActivateTrial);
        setCreatedAt(data.createdAt);
        // if (data.coverUrl && currentUser) {
        //   setCover({
        //     url: data.coverUrl,
        //     thumbnail: data.coverThumbnail,
        //   });
        // }
        setLoadingStatus('');

        if (!hasSubscription) {
          statReachGoal('adult_paywall', {
            trial: data.canActivateTrial,
          });
        }
      })
      .catch(() => {
        setTimeout(() => loadRates(), 3000);
      });
  }, [hasSubscription, targetModelId, currentUser]);

  useEffect(() => {
    statReachGoal('adult_paywall', {
      trial: canActivateTrial,
    });
    setStorageItem('contentPriorityType', ContentPriorityType.ADULT);
    loadRates();
  }, []);

  const ratesInfo = useMemo(() => {
    rates.sort((a, b) => (a.price > b.price ? -1 : 1));

    let minPrice = rates[rates.length - 1].price;
    const result: RateInfo[] = [];
    for (let rate of rates) {
      const monthPrice = rate.price / rate.period;
      result.push({
        period: rate.period,
        monthPrice: monthPrice,
        sale: Math.round(((minPrice - monthPrice) / minPrice) * 100),
      });
    }

    return result;
  }, [rates]);

  const handleBuy = useCallback(() => {
    statReachGoal('adult_subscription_btn_click', {
      trial: canActivateTrial,
    });

    if (!isLogged) {
      dispatch(profileActions.setAuthPopoverState(AuthPopoverState.REGISTER));
      return;
    }

    const rate = rates[rateSelected];
    if (canActivateTrial) {
      dispatch(
        balanceActions.pay({
          amount: rate.price,
          action: BalancePayAction.TRIAL_SUBSCRIPTION_ADULT,
          objectId: SubscriptionType.Adult,
          extraId: targetModelId,
          period: rate.period,
        }),
      );
    } else {
      dispatch(
        balancePayActions.open({
          amount: rate.price,
          action: BalancePayAction.SUBSCRIPTION_ADULT,
          objectId: SubscriptionType.Adult,
          period: rate.period,
        }),
      );
    }
  }, [rates, rateSelected, canActivateTrial]);

  const coverUrls = useMemo(() => {
    if (cover && !!cover.url) {
      return [cover.url, cover.thumbnail].map((url) => `url(${url})`).join(', ');
    } else if (targetModel) {
      return `url(${targetModel.photo})`;
    } else {
      return '';
    }
  }, [cover]);

  const handleCoverClick = useCallback(() => {
    if (targetModelId > 0) {
      goTo(PROFILE, { id: targetModelId });
    }
  }, [targetModelId]);

  const handlePressActivated = useCallback(() => {
    if (targetModelId > 0) {
      goTo(PROFILE, { id: targetModelId });
    } else {
      goTo(FEED);
    }
  }, [targetModelId]);

  if (decryptKeys[-1]) {
    return (
      <Placeholder
        centered
        title={t('subscription_success_title')}
        icon={<IcSuccess72 />}
        actions={
          <Button onClick={handlePressActivated}>
            {t('subscription_success_button_go_to_feed')}
          </Button>
        }
      >
        {t('subscription_success_adult_caption')}
      </Placeholder>
    );
  }

  function renderFooter() {
    if (Date.now() < trialDeadline && canActivateTrial && currentUser) {
      return (
        <List>
          <ListItem
            className="Paywall__trial"
            color="purple"
            onClick={handleBuy}
            description={
              <>
                <div className="Paywall__trial__description">
                  {t('paywall_trial_limited_caption', {
                    sale: Math.floor(100 - (trialPrice / ratesInfo[2].monthPrice) * 100),
                  })}
                </div>
                <div className="Paywall__trial__timer">
                  <div className="Paywall__trial__timer__icon">
                    <IcClock28 />
                  </div>
                  <div className="Paywall__trial__timer__text">
                    <PaywallTimer deadline={trialDeadline} />
                  </div>
                </div>
              </>
            }
            chevron
          >
            {t('paywall_trial_limited_title', {
              price: trialPrice,
            })}
          </ListItem>
        </List>
      );
    }

    return (
      <>
        <div className="Paywall__rates">
          {ratesInfo.map((rate, index) => (
            <div
              className={classNames({
                Paywall__rate: true,
                selected: rateSelected === index,
              })}
              key={index}
              onClick={() => setRateSelected(index)}
            >
              {!!rate.sale && <div className="Paywall__rate__badge">-{rate.sale}%</div>}
              <div className="Paywall__rate__period">{rate.period}</div>
              <div className="Paywall__rate__period_label">
                {rate.period == 1
                  ? t('paywall_period_month')
                  : t('paywall_period_months')}
              </div>
              <div className="Paywall__rate__price">
                {formatCurrency(rate.monthPrice, CurrencyEnum.EUR)}/
                {t('paywall_period_month')}
              </div>
            </div>
          ))}
        </div>
        <Button fillColor="purple" onClick={handleBuy} flare>
          {t('continue')}
        </Button>
      </>
    );
  }

  if (loadingStatus === 'loading') {
    return <Loader isWrapped />;
  }

  return (
    <>
      <Header showCloseButton={!skipClose} isTransparent isSecondaryBg />
      <ScrollView>
        <div className="Paywall">
          {!!cover && !!cover.url && cover.url.indexOf('.mp4') > -1 ? (
            <div className="Paywall__cover">
              <video
                autoPlay
                muted
                loop
                playsInline
                controls={false}
                src={cover.url}
                poster={cover.thumbnail}
              />
            </div>
          ) : (
            <div
              className={classNames({
                Paywall__cover: true,
              })}
              style={{ backgroundImage: coverUrls }}
              onClick={handleCoverClick}
            />
          )}
          <div className="Paywall__info">
            <div className="Paywall__slider_wrap">
              <Swiper
                spaceBetween={16}
                slidesPerView={1}
                autoplay={{
                  delay: 10000,
                }}
                loop
                onSlideChange={(swiper) => setActiveSlide(swiper.realIndex)}
              >
                {slides.map((slide, index) => {
                  const Icon = slide.icon;
                  return (
                    <SwiperSlide key={index}>
                      <div className="Paywall__slide">
                        {/*<div className="Paywall__slide__icon">*/}
                        {/*  {targetModel && slide.canShowAvatar ? (*/}
                        {/*    <div*/}
                        {/*      className="Paywall__slide__photo"*/}
                        {/*      style={{ backgroundImage: `url(${targetModel.photo})` }}*/}
                        {/*    >*/}
                        {/*      <div className="Paywall__slide__photo__icon">*/}
                        {/*        <Icon />*/}
                        {/*      </div>*/}
                        {/*    </div>*/}
                        {/*  ) : (*/}
                        {/*    <Icon />*/}
                        {/*  )}*/}
                        {/*</div>*/}
                        <div className="Paywall__slide__title">{slide.title}</div>
                        <div className="Paywall__slide__caption">{slide.caption}</div>
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            </div>
            <div className="Paywall__slider_bullets">
              {slides.map((_, index) => (
                <div
                  className={classNames({
                    Paywall__slider_bullet: true,
                    active: index === activeSlide,
                  })}
                  key={index}
                />
              ))}
            </div>
          </div>
          <div className="Paywall__footer">
            {loadingStatus === 'loading' && (
              <div className="Paywall__footer__loading">
                <Loader />
              </div>
            )}
            {renderFooter()}
            <div className="Paywall__trialSubscription">
              {canActivateTrial
                ? t('paywall_trial_description', {
                    price: trialPrice,
                    amount: formatCurrency(
                      ratesInfo[rateSelected].monthPrice * ratesInfo[rateSelected].period,
                      CurrencyEnum.EUR,
                    ),
                  })
                : t('subscription_personal_button_description')}
            </div>
          </div>
        </div>
      </ScrollView>
      <AuthPopoverSimple />
    </>
  );
};
